/**
 * German translations (primary language).
 * Namespace-based organization: common, tenants, auth, errors.
 */
const de = {
  common: {
    appName: "openDesk Operator",
    navigation: {
      dashboard: "Dashboard",
      tenants: "Mandanten",
      capacity: "Kapazitat",
      audit: "Audit-Protokoll",
      settings: "Einstellungen",
    },
    actions: {
      save: "Speichern",
      cancel: "Abbrechen",
      delete: "Loschen",
      create: "Erstellen",
      edit: "Bearbeiten",
      refresh: "Aktualisieren",
      confirm: "Bestatigen",
      back: "Zuruck",
      next: "Weiter",
      close: "Schliessen",
      search: "Suchen",
      filter: "Filtern",
      export: "Exportieren",
      retry: "Erneut versuchen",
    },
    status: {
      ready: "Bereit",
      provisioning: "Provisionierung",
      degraded: "Eingeschrankt",
      error: "Fehler",
      pending: "Ausstehend",
      deleting: "Wird geloscht",
      updating: "Aktualisierung",
      unknown: "Unbekannt",
    },
    confirm: {
      deleteTitle: "Loschen bestatigen",
      deleteMessage: "Sind Sie sicher, dass Sie dieses Element loschen mochten? Diese Aktion kann nicht ruckgangig gemacht werden.",
      yes: "Ja, loschen",
      no: "Nein, abbrechen",
    },
    theme: {
      dark: "Dunkles Thema",
      light: "Helles Thema",
      toggle: "Thema wechseln",
    },
    language: {
      label: "Sprache",
      de: "Deutsch",
      en: "English",
      toggle: "Sprache wechseln",
    },
    loading: "Laden...",
    phaseUnknown: "Unbekannt",
    pageLoading: "Seite wird geladen",
    authLoading: "Authentifizierung wird geladen",
    noData: "Keine Daten vorhanden",
    lastFetched: "Zuletzt aktualisiert",
    justNow: "gerade eben",
  },
  dashboard: {
    title: "Cluster Overview",
    activeTenants: "Aktive Mandanten",
    operatorHealth: "Operator Health",
    crdsRegistered: "CRDs registriert",
    podsRunning: "Pods Running",
    tenantStatus: "Mandanten-Status",
    recentEvents: "Letzte Ereignisse",
    clusterResources: "Cluster-Ressourcen",
    noEvents: "Keine Ereignisse.",
    driftDetected: "Drift erkannt",
    components: "Komponenten",
  },
  detail: {
    title: "Mandanten-Details",
    provisioning: "Provisionierung",
    appsReady: "Apps bereit",
    created: "Erstellt",
    conditions: "Bedingungen",
    actions: "Aktionen",
    startBackup: "Backup starten",
    deleteTenant: "Mandant loschen",
    backupStarted: "Backup gestartet.",
    deleteConfirmTitle: "Mandant loschen",
    deleteConfirmMessage: "Der Mandant und alle zugehorigen Ressourcen werden unwiderruflich geloscht.",
    typeToConfirm: "Geben Sie den Namen ein",
  },
  tenants: {
    title: "Mandanten",
    create: "Mandant erstellen",
    detail: "Mandanten-Details",
    name: "Name",
    displayName: "Anzeigename",
    domain: "Domain",
    edition: "Edition",
    phase: "Phase",
    apps: "Anwendungen",
    readyApps: "Bereite Anwendungen",
    totalApps: "Gesamte Anwendungen",
    namespace: "Namespace",
    createdAt: "Erstellt am",
    userQuota: "Benutzerquota",
    storageQuota: "Speicherquota",
    branding: "Branding",
    backup: "Sicherung",
    triggerBackup: "Sicherung starten",
    update: "Aktualisierung",
    triggerUpdate: "Aktualisierung starten",
    targetVersion: "Zielversion",
    monitoring: "Uberwachung",
    conditions: "Bedingungen",
    config: "Konfiguration",
  },
  nav: {
    main: "Hauptnavigation",
    skipToContent: "Zum Hauptinhalt springen",
    userMenu: "Benutzermenu",
    sovereigntyStatus: "Souveranitatsstatus",
    cluster: "Cluster",
    domain: "Domain",
    phase: "Phase",
    installed: "Installiert",
    section: {
      platform: "Plattform",
      governance: "Governance",
      system: "System",
      tenant: "Mandant",
      configuration: "Konfiguration",
    },
    dashboard: "Dashboard",
    tenants: "Mandanten",
    components: "Komponenten",
    upgrades: "Upgrades",
    backupDr: "Backup & DR",
    platformPolicy: "Plattform-Policy",
    monitoring: "Monitoring",
    auditLog: "Audit-Log",
    operator: "Operator",
    settings: "Einstellungen",
    overview: "\u00DCbersicht",
    deployments: "Deployments",
    apps: "Apps",
    users: "Nutzer & Gruppen",
    config: "Konfiguration",
    iam: "IAM & Auth",
    backup: "Backup",
    audit: "Audit",
  },
  ladder: {
    title: "Deploy-Leiter",
    subtitle:
      "Jede Komponente steht in der Stufe, die der Release-Resolver ihr gegeben hat. Die Stufen laufen von unten nach oben; eine Stufe wartet auf die darunter.",
    pickTenant: "Für die Leiter einen einzelnen Mandanten wählen.",
    empty: "Keine Komponenten in diesem Namespace.",
    stageLabel: "Stufe",
    stage: {
      s10: "Vor-Migrationen",
      s30: "Infrastruktur",
      s50: "Anwendungen",
      s60: "Bootstrap",
      s90: "Nach-Migrationen",
      unstaged: "Ohne Stufe",
    },
    state: {
      installed: "Installiert",
      progressing: "Läuft",
      failed: "Fehlgeschlagen",
      idle: "Abgeschaltet",
    },
    detail: {
      title: "Komponenten-Detail",
      none: "Nichts ausgewählt",
      hint:
        "Wähle eine Komponente in der Leiter aus, um Chart, Version, Abhängigkeiten und das zugehörige Values-Secret zu sehen.",
      chart: "Chart",
      version: "Version",
      requires: "Wartet auf",
      requiresNone: "nichts",
      values: "Values",
      idleHint:
        "Diese Komponente ist für den Mandanten abgeschaltet. Sie installiert nie, und abhängige Komponenten werten sie als erfüllt, damit die Leiter nicht blockiert.",
      readyHint:
        "Das Values-Secret speist die L1+L2-Ebene. Fehlt es, installiert Helm mit den Chart-Standardwerten.",
    },
  },
  auth: {
    login: "Anmelden",
    logout: "Abmelden",
    sessionExpired: "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.",
    unauthorized: "Sie haben nicht die erforderlichen Berechtigungen fur diese Aktion.",
    welcome: "Willkommen",
    role: {
      "platform-admin": "Plattform-Administrator",
      "tenant-admin": "Mandanten-Administrator",
      "tenant-user": "Mandanten-Benutzer",
    },
  },
  errors: {
    accessDeniedTitle: "Zugriff verweigert",
    accessDenied: "Zugriff verweigert. Sie haben keine Berechtigung für diese Seite.",
    generic: "Ein unerwarteter Fehler ist aufgetreten.",
    network: "Netzwerkfehler. Bitte uberprufen Sie Ihre Verbindung.",
    notFound: "Die angeforderte Ressource wurde nicht gefunden.",
    forbidden: "Sie haben keine Berechtigung fur diese Aktion.",
    conflict: "Ein Konflikt ist aufgetreten. Die Ressource existiert moglicherweise bereits.",
    validation: "Bitte uberprufen Sie Ihre Eingaben.",
    serverError: "Serverfehler. Bitte versuchen Sie es spater erneut.",
    timeout: "Die Anfrage hat zu lange gedauert. Bitte versuchen Sie es erneut.",
    rateLimit: "Zu viele Anfragen. Bitte warten Sie einen Moment.",
  },
} as const;

export default de;

/** Recursive type that extracts the shape of a translation object without literal string types. */
type DeepStringShape<T> = {
  [K in keyof T]: T[K] extends string ? string : DeepStringShape<T[K]>;
};

/** Type for translation keys derived from the German translations structure. */
export type TranslationKeys = DeepStringShape<typeof de>;
